// db-core.js

const SHOP_DOMAIN = window.location.hostname;
const GRAPHQL_ENDPOINT = "https://gear-db-proxy.onrender.com/graphql";

// ─── Pagination Settings for GraphQL Fetch ───
const PAGE_SIZE       = 100;  // items per GraphQL request (Shopify max)
const MAX_PAGES       = 50;   // max loops to guard infinite pagination
const BASE_BACKOFF_MS = 200;  // initial retry delay in ms (exponential)

// Allowed product types
const allowedTypes = ["Mousepads"];

// Global state
let allProducts = [];
let selectedProductsForComparison = [];
let currentProductType = null;
let currentFilteredProducts = [];
let currentSortColumn = null;
let currentSortAsc = true;
let showingFavoritesOnly = false;
let activeDropdownColumn = null;
let lastRenderedProductSet = null;
let savedComparisons = loadLocal("savedComparisons", []);
let favoritedProducts = loadLocal("favoritedProducts", []);
let activeComparisonName = null;
let activeFavoriteName = null;
let hiddenColumns = [];
let columnOrder = [];
let tierListDraft = loadLocal("tierList", {});
const productMap = {};
const recentlyViewedMap = {};
const wrapper = document.getElementById("products-table-wrapper");

// -- Radar Chart specs --
const radarChartFieldsByType = {
  Mice: ["Weight", "Polling Rate", "CPI/DPI"],
  Mousepads: ["Length (mm)", "Width (mm)", "Height (mm)"],
  "Grip Tape": ["Thickness"],
  "Mouse Feet": ["Thickness"],
  "Arm Sleeves": ["Size"],
};

// -- Match % per category --
const matchPercentFieldsByType = {
  Mousepads: [
    //{ label: "Size", prefix: "P-Size__" },
    { label: "Length (mm)", prefix: "Dims__", part: 0 },
    { label: "Width (mm)", prefix: "Dims__", part: 1 },
    { label: "Thickness (mm)", prefix: "Dims__", part: 2 },
    { label: "Type", prefix: "Surface Type__" },
    { label: "Surface", prefix: "Surface Material_" },
    { label: "Coating", prefix: "Surface Coating__" },
    { label: "Texture", prefix: "Surface Texture__" },
    { label: "Firmness", prefix: "Firmness__" },
    { label: "Base", prefix: "Base Material__" },
    { label: "Stitched", prefix: "Stitched Edges_" },
    { label: "Lighting", prefix: "Lighting_" },
  ],
  "Mouse Feet": [
    { label: "Thickness", prefix: "Thickness__" },
    { label: "Material", prefix: "Material__" },
    { label: "Rounded Edges", prefix: "Rounded Edges__" },
    { label: "Colour", prefix: "Colour__" },
  ],
};

// ── Shared colour map for swatches ──
window.DB_COLOUR_MAP = {
  Black: "#000000",
  White: "#ffffff",
  Red: "#ff0000",
  Gold: "#ffd700",
  "Dark Blue": "#00008b",
  "Deep Blue": "#0006b1",
  "Amnis Blue": "#277CE0",
  "Blue Screen": "#0047bb",
  "Ice Blue": "#739BD0", 
  "Soft Blue": "#93CAED", 
  "Forest Green": "#228b22",
  "Green Screen": "#00b140",
  "Matcha Green": "#74A662",
  "Coffee Brown": "#6F4E37",
  "Wine Red": "#722F37",
  "Rich Red": "#E11218",
  "Daidai Orange": "#EE7800",
  "Multi-coloured":
    "linear-gradient(90deg, red, orange, yellow, green, blue, indigo, violet)",
  // …add any others here…
};

// -- Global utility functions --

// Safe localStorage load
function loadLocal(key, fallback = null) {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
}

function copyCurrentUrlToClipboard() {
  const url = window.location.href;

  if (navigator.clipboard) {
    navigator.clipboard
      .writeText(url)
      .then(() => alert("Link copied to clipboard!"))
      .catch(() => fallbackCopy(url));
  } else {
    fallbackCopy(url);
  }
}

function fallbackCopy(url) {
  const tempInput = document.createElement("input");
  tempInput.value = url;
  document.body.appendChild(tempInput);
  tempInput.select();
  document.execCommand("copy");
  document.body.removeChild(tempInput);
  alert("Link copied!");
}

function renderColorSwatches(colorString) {
  if (!colorString) return "";

  return colorString
    .split("•")
    .map((color) => {
      const rawColor = color.trim().toLowerCase();
      const safeColor = rawColor.replace(/[^a-z0-9#(),.%\- ]/gi, "");
      const isValid = /^#[a-f0-9]{3,6}$|^[a-z]+$/i.test(safeColor);
      if (!isValid) return "";
      return `<div class="styled-box" title="${escapeHTML(
        safeColor
      )}" style="background-color: ${safeColor};"></div>`;
    })
    .join("");
}

// Safe localStorage save
function saveLocal(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (err) {
    console.error(`Failed to save ${key}`, err);
  }
}

// Class toggle shortcut
function toggleClass(el, className) {
  if (el) el.classList.toggle(className);
}

// Open a modal
function openModal(id) {
  const modal = document.getElementById(id);
  if (!modal) return;

  // 1) Ensure it’s visible
  modal.style.display = 'flex';

  // 2) Swap in the “opening” class
  modal.classList.remove('closing');
  modal.classList.add('opening');
}

// Close modal
function closeModal(id) {
  const modal = document.getElementById(id);
  if (!modal) return;

  // 1) Swap out “opening” for “closing”
  modal.classList.remove('opening');
  modal.classList.add('closing');

  // 2) After the fade-out completes, hide it for real
  function onAnimEnd(e) {
    // only run once, on the modal wrapper itself
    if (e.target === modal) {
      modal.style.display = 'none';
      modal.classList.remove('closing');
      modal.removeEventListener('animationend', onAnimEnd);
    }
  }

  modal.addEventListener('animationend', onAnimEnd);
}

// HTML escaping for security
function escapeHTML(str) {
  return str.replace(
    /[&<>"']/g,
    (m) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#039;" }[
        m
      ])
  );
}

// Extend this colorMap if you use more named colours in tags
function getColorHexFromName(name) {
  const colorMap = {
    Black: "#000000",
    White: "#ffffff",
    Red: "#ff0000",
    Blue: "#0000ff",
    "Dark Blue": "#00008B",
    Green: "#00ff00",
    Yellow: "#ffff00",
    Orange: "#ffa500",
    Pink: "#ffc0cb",
    Purple: "#800080",
    Gray: "#808080",
    Brown: "#8b4513",
    Teal: "#008080",
    Gold: "#ffd700",
    Silver: "#c0c0c0",
    // ✅ Add any additional colours you use
  };

  return colorMap[name] || "#cccccc"; // default grey if unknown
}

// Debounce wrapper for events
function debounce(fn, delay) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), delay);
  };
}

// Extend this mapping for vendor display overrides as needed
function getDisplayVendor(name) {
  if (!name) return "";

  const mappings = {
    "Glorious PC Gaming Race": "Glorious",
    "Tiger Gaming": "ESPTiger",
  };

  return mappings[name.trim()] || name.trim();
}

function getColumnOrder(type) {
  return loadLocal(`columnOrder_${type}`, null);
}

function getHiddenColumns(type) {
  return loadLocal(`hiddenColumns_${type}`, []);
}

function updateURLFromFilters() {
  if (window._deepLinking) return;

  const params = new URLSearchParams();

  if (currentProductType) {
    params.set("type", currentProductType);
  }

  document.querySelectorAll(".selected-filter").forEach((span) => {
    const val = span.textContent.trim();
    if (!val) return;
    params.set(span.dataset.column, val);
  });

  const qs = params.toString();
  const newUrl = qs
    ? `${window.location.pathname}?${qs}`
    : window.location.pathname;

  history.replaceState({}, "", newUrl);
}

// Bind once for all tooltips
(function () {
  if (document.body._tooltipBound) return;
  document.body._tooltipBound = true;

  let current;
  document.addEventListener("mouseover", (e) => {
    const el = e.target.closest(".db-tooltip");
    if (!el) return;
    const html = el.getAttribute("data-tooltip-html");
    if (!html) return;
    const box = document.createElement("div");
    box.className = "tooltip-box";
    box.innerHTML = html;
    const rect = el.getBoundingClientRect();
    box.style.position = "absolute";
    box.style.top = `${rect.bottom + window.scrollY + 12}px`;
    box.style.left = `${rect.left + window.scrollX}px`;
    document.body.appendChild(box);
    current = { el, box };
  });
  document.addEventListener("mouseout", (e) => {
    if (!current) return;
    if (!e.relatedTarget || !current.box.contains(e.relatedTarget)) {
      current.box.remove();
      current = null;
    }
  });
})();

(function () {
  const _orig = updateURLFromFilters;
  let _timer;
  updateURLFromFilters = function () {
    // ❌ guard here too
    if (window._deepLinking) return;

    clearTimeout(_timer);
    _timer = setTimeout(() => {
      _orig();
    }, 200);
  };
})();

function getTypeIcon(type) {
  switch (type) {
    case "Mice":
      return "https://cdn.shopify.com/s/files/1/0102/8426/9615/files/gaming_mouse_icon_213e02fd-1351-424c-9bd0-ae4ae380766d.svg?v=1745069907";
    case "Mousepads":
      return "https://cdn.shopify.com/s/files/1/0102/8426/9615/files/mousepad_icon_akira_EG.svg?v=1745070537";
    case "Grip Tape":
      return "https://cdn.shopify.com/s/files/1/0102/8426/9615/files/grip_tape_mouse_icon_a07cf236-ed23-4e44-b094-1a9d79969fd4.svg?v=1745080420";
    case "Mouse Feet":
      return "https://cdn.shopify.com/s/files/1/0102/8426/9615/files/mouse_skates_icon_d4018da7-945b-4482-9c86-1f5d47fc133d.svg?v=1745080749";
    case "Arm Sleeves":
      return "https://cdn.shopify.com/s/files/1/0102/8426/9615/files/arm_sleeve_icon_180f86ae-175e-428e-bad8-d6d949913b34.svg?v=1745082138";
    default:
      return "";
  }
}

document.addEventListener('DOMContentLoaded', function() {
  const sel        = document.getElementById('contact_type');
  const emailField = document.querySelector('input[name="contact[email]"]');
  const summary    = document.querySelector('#summaryField textarea');
  const ids        = ['brandFields','bugFields','relatedGearField','summaryField','submitBtn'];

  // 1) Always make email mandatory
  if (emailField) emailField.required = true;

  function reset() {
    // hide all panels
    ids.forEach(id => document.getElementById(id)?.classList.add('hidden'));
    // clear summary-required
    if (summary) summary.required = false;
  }

  sel.addEventListener('change', function() {
    reset();
    const v = this.value;
    if (!v) return;

    if (v === 'brand') document.getElementById('brandFields').classList.remove('hidden');
    if (v === 'bug')   document.getElementById('bugFields').classList.remove('hidden');
    if (v === 'info')  document.getElementById('relatedGearField').classList.remove('hidden');

    if (['feature','info','other'].includes(v)) {
      document.getElementById('summaryField').classList.remove('hidden');
      if (summary) summary.required = true;
    }

    document.getElementById('submitBtn').classList.remove('hidden');
  });
});